<script setup>
import { computed, defineProps, defineEmits, ref, watch } from 'vue'

const props = defineProps({
  /**
     * * Emotion to display, can be one of:
     * - 'very-happy'
     * - 'happy'
     * - 'neutral'
     * - 'sad'
     * - 'very-sad'
     */
  emotion: {
    type: String,
    default: 'neutral',
    validator: value => [ 'very-happy', 'happy', 'neutral', 'sad', 'very-sad' ].includes(value)
  },
  /**
   * Size of the emotion image, default is '15px'
   */
  size: {
    type: String,
    default: '15px'
  },
  /**
  * Whether the emotion is clickable or not, default is false
   */
  isClickable: {
    type: Boolean,
    default: false
  },
  /**
  * Whether the emotion is selected or not, default is false
   */
  isSelected: {
    type: Boolean,
    default: false
  },

  /**
   * Whether the emotion is disabled or not, default is false
   */

  isDisabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([ 'set-emotion' ])

const type = ref(props.isClickable ? 'enabled' : 'pressed')
const svgContent = ref('')
const svgRef = ref(null)
let svgTimeout = null

const getEmotionPath = emotion =>
  `/img/emotions/${emotion}/${type.value}.svg`

const fetchSvg = async () => {
  const path = getEmotionPath(props.emotion)
  try {
    const res = await fetch(path)
    svgContent.value = await res.text()
  } catch (e) {
    svgContent.value = ''
  }
}

watch([ () => props.emotion, type ], fetchSvg, { immediate: true })

const classes = computed(() => [
  'c-emotion',
  { 'pressed': type.value === 'pressed' },
  { 'pressed-hover': type.value === 'pressed-hover' },
  { 'hover': type.value === 'hover' },
  { 'enabled': type.value === 'enabled' },
  { [props.emotion]: true }
])

const styles = computed(() => ({
  width: props.size,
  height: props.size,
  cursor: props.isClickable ? 'pointer' : 'default'
}))

watch([ () => props.isSelected, () => props.isDisabled ], ([ selected, disabled ]) => {
  if (selected && disabled) {
    type.value = 'pressed-disabled'
  } else if (selected) {
    type.value = 'pressed'
  } else {
    type.value = props.isClickable ? 'enabled' : 'pressed'
  }
}, { immediate: true })

const handleClick = () => {
  if (props.isClickable && !props.isDisabled) {
    type.value = type.value === 'hover' ? 'pressed' : 'hover'
    if (type.value === 'pressed') {
      emit('set-emotion', props.emotion)
    } else {
      emit('set-emotion', null)
    }
  }
}

const handleMouseEnter = () => {
  if (props.isClickable && type.value === 'enabled') {
    type.value = 'hover'
  }
  if (props.isClickable && type.value === 'pressed') {
    type.value = 'pressed-hover'
  }
}

const handleMouseLeave = () => {
  if (props.isClickable && type.value === 'hover') {
    type.value = 'enabled'
  }
  if (props.isClickable && type.value === 'pressed-hover') {
    type.value = 'pressed'
  }
}

watch(svgContent, async () => {
  if (svgTimeout) clearTimeout(svgTimeout)
  if (svgRef.value) {
    svgTimeout = setTimeout(() => {
      if (svgRef.value && svgContent.value) {
        svgRef.value.innerHTML = svgContent.value
        const innerSvg = svgRef.value.querySelector('svg')
        if (innerSvg) {
          innerSvg.removeAttribute('width')
          innerSvg.removeAttribute('height')
          innerSvg.setAttribute('width', '100%')
          innerSvg.setAttribute('height', '100%')
          innerSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet')

          innerSvg.setAttribute('data-testid', `c-emotion-${props.emotion}`)
        }
      }
    }, 200)
  }
})
</script>

<template>
  <svg
    ref="svgRef"
    :class="classes"
    :style="styles"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    xmlns="http://www.w3.org/2000/svg"
    focusable="false"
  />
</template>

<style lang="scss">
$c-emotions: (
  "very-happy": #34D889,
  "happy": #B2DD3C,
  "neutral": #FFC24B,
  "sad": #FF974B,
  "very-sad": #FF4B8C
);

$base-opacity: 0.3;
$hover-opacity: $base-opacity + 0.2;

.c-emotion {
  &.hover,
  &.pressed,
  &.pressed-hover {
    animation: fadeTo 0.2s;
  }

  @each $emotion, $color in $c-emotions {
    &.pressed.#{$emotion} {
      filter: drop-shadow(0px 12px 10px rgba($color, $base-opacity));
    }

    &.pressed-hover.#{$emotion} {
      filter: drop-shadow(0px 12px 10px rgba($color, $hover-opacity));
    }
  }
}

svg.c-emotion, .c-emotion svg {
  overflow: visible !important;
  overflow-clip-margin: unset !important;
}

@keyframes fadeTo {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

</style>
